// Pure helpers for the composer's Attach control, extracted so a
// node-pool test can exercise them without mounting React (same pattern
// as parties-panel-helpers.ts).
//
// Deliberately imports NOTHING — not even a type from web/src/api,
// which would drag api.ts (import.meta.env, Workers-typed fetch) into
// the root tsconfig program the node-pool tests compile under. The ref
// shape below is structurally identical to api.ts's
// OutboundAttachmentRef, so values flow between them freely.
//
// What these decide matters more than their size: splitAttachments is
// what determines which files actually leave with the message. A file
// staged in the UI but dropped here would be a message that looks sent
// with its paperwork and isn't.

// Mirror of api.ts's OutboundAttachmentRef (structural, not imported).
interface OutboundAttachmentRef {
  source: "document" | "onedrive" | "sharepoint" | "staged";
  id: string;
}

// One file staged on the message. Bytes are carried inline only for a
// SMALL file off the advisor's computer; everything else travels as a
// reference the server resolves at send time.
//
// "staged" is the fourth source, and it is a computer file too — one
// big enough that carrying it inside the send request would be a
// memory problem rather than a transfer (see
// shared/email/staged-attachments.ts). It was uploaded ahead of the
// send and now travels as an id like any other reference.
export interface PendingAttachment {
  key: string;
  name: string;
  // Raw size in bytes. 0 when the source didn't tell us (a SharePoint
  // search hit) — the server still enforces the real total.
  bytes: number;
  contentType: string;
  source: "computer" | "document" | "onedrive" | "sharepoint" | "staged";
  contentBase64?: string;
  refId?: string;
  // Set while a big file is still on its way up. A message must not
  // send with one of these on it — the reference wouldn't resolve.
  uploading?: boolean;
  // 0-100 while uploading, for the chip's progress bar.
  progress?: number;
}

/** Split staged attachments into the two shapes the API takes. */
export function splitAttachments(list: PendingAttachment[]): {
  attachments: Array<{
    name: string;
    contentType: string;
    contentBase64: string;
  }>;
  attachmentRefs: OutboundAttachmentRef[];
} {
  const attachments: Array<{
    name: string;
    contentType: string;
    contentBase64: string;
  }> = [];
  const attachmentRefs: OutboundAttachmentRef[] = [];
  for (const a of list) {
    // A file still uploading has no id yet — including it would send a
    // reference to nothing. The composer blocks Send while any chip is
    // in this state; this is the second line of that same defense.
    if (a.uploading) continue;
    if (a.source === "computer" && a.contentBase64) {
      attachments.push({
        name: a.name,
        contentType: a.contentType,
        contentBase64: a.contentBase64,
      });
    } else if (a.refId && a.source !== "computer") {
      attachmentRefs.push({ source: a.source, id: a.refId });
    }
  }
  return { attachments, attachmentRefs };
}

export function totalAttachmentBytes(list: PendingAttachment[]): number {
  return list.reduce((sum, a) => sum + a.bytes, 0);
}

/**
 * Bytes riding INSIDE the send request as base64 — the only pile whose
 * size the browser and the Worker both have to hold in memory.
 *
 * This is what decides whether the next picked file rides along or gets
 * uploaded on its own first. Deliberately not "total attached": a
 * 15 MB file already staged in R2 costs the send request nothing.
 */
export function inlineAttachmentBytes(list: PendingAttachment[]): number {
  return list.reduce(
    (sum, a) => (a.source === "computer" ? sum + a.bytes : sum),
    0,
  );
}
